<script setup lang="ts">
/**
 * StructuredAddressInput — 단일 textarea 모드 ↔ 구조화 입력 모드 토글.
 *
 * - 기본 textarea 모드: 자유 입력 또는 자동 prefill 결과 표시.
 * - "Edit" → 구조화 입력 박스: Address Line 1/2/3 + Country / State / City / Postcode (cascading dropdowns).
 *     ChatMalaysiaAddressInput 와 동일한 데이터 소스 (baseCodeApi / entityMyApi).
 *     입력 시 자동으로 composed string 으로 합쳐 v-model 에 반영.
 * - "Cancel" → textarea 모드 복귀. 합성된 값 유지.
 * - 첫 Edit 진입 시 textarea 의 현재 값을 best-effort 로 파싱 + dropdown 매칭.
 */
import { ref, watch, computed, onMounted, nextTick } from 'vue';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';

const MALAYSIA_CODE = 'B15135';

const props = defineProps<{
  modelValue: string;
  label: string;
  draggingActive?: boolean;
  showLabel?: boolean;
  /** 회사 주소 목록 — "Address History" 버튼 클릭 시 챗봇에 표시. */
  addresses?: any[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'drop-fill', value: string): void;
  (e: 'show-addresses'): void;
}>();

const editMode = ref(false);
/** Edit 진입 시 원래 textarea 값 스냅샷 — Cancel 시 복원용. */
const originalValue = ref('');

// 자유 텍스트 라인 필드
const line1 = ref('');
const line2 = ref('');
const line3 = ref('');

// 코드 필드 (dropdown 선택값)
const countryCode = ref('');
const stateCode = ref('');
const cityCode = ref('');
const postcodeCode = ref('');
// 외국 주소용 free text fallback
const foreignState = ref('');
const foreignCity = ref('');
const foreignPostcode = ref('');

// dropdown 옵션 목록
interface CountryOption { TOTAL_CODE: string; CODE_NAME: string }
interface LocOption { code: string; name: string }
const countryOptions = ref<CountryOption[]>([]);
const stateOptions = ref<LocOption[]>([]);
const cityOptions = ref<LocOption[]>([]);
const postcodeOptions = ref<LocOption[]>([]);

const loadingCountries = ref(false);
const loadingStates = ref(false);
const loadingCities = ref(false);
const loadingPostcodes = ref(false);

const isMalaysiaSelected = computed(() => countryCode.value === MALAYSIA_CODE);

// 표시명 — composed string 합성/preview 에 사용.
const countryName = computed(() => countryOptions.value.find((o) => o.TOTAL_CODE === countryCode.value)?.CODE_NAME || '');
const stateName = computed(() => {
  if (isMalaysiaSelected.value) return stateOptions.value.find((o) => o.code === stateCode.value)?.name || '';
  return foreignState.value.trim();
});
const cityName = computed(() => {
  if (isMalaysiaSelected.value) return cityOptions.value.find((o) => o.code === cityCode.value)?.name || '';
  return foreignCity.value.trim();
});
const postcodeName = computed(() => {
  if (isMalaysiaSelected.value) {
    const f = postcodeOptions.value.find((o) => o.code === postcodeCode.value);
    return f?.name || postcodeCode.value || '';
  }
  return foreignPostcode.value.trim();
});

function composeAddress(): string {
  const pc = postcodeName.value;
  const cityLine = [pc, cityName.value].filter(Boolean).join(' ');
  return [
    line1.value.trim(),
    line2.value.trim(),
    line3.value.trim(),
    cityLine,
    stateName.value,
    countryName.value,
  ]
    .map((s) => s.replace(/^[\s,.;]+|[\s,.;]+$/g, '').trim())
    .filter(Boolean)
    .join(', ');
}

// ── Initial data load (한 번만) ──
onMounted(async () => {
  loadingCountries.value = true;
  try {
    const res: any = await baseCodeApi.getCodesByMainCode('CM', 'B15');
    const codes = res?.codes || res?.data?.codes || [];
    countryOptions.value = codes
      .map((c: any) => ({
        TOTAL_CODE: String(c.totalCode || c.TOTAL_CODE || '').trim(),
        CODE_NAME: String(c.defaultCodeName || c.DEFAULT_CODE_NAME || c.codeName || c.CODE_NAME || '').trim().toUpperCase(),
      }))
      .filter((c: CountryOption) => c.TOTAL_CODE && c.CODE_NAME && !c.TOTAL_CODE.endsWith('000'));
  } catch (err) {
    console.warn('[StructuredAddressInput] load countries failed:', err);
  } finally {
    loadingCountries.value = false;
  }

  loadingStates.value = true;
  try {
    const res: any = await entityMyApi.getMalaysiaStates();
    const data = res?.data?.data || res?.data || [];
    stateOptions.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.warn('[StructuredAddressInput] load states failed:', err);
  } finally {
    loadingStates.value = false;
  }
});

const isReverseLookup = ref(false);

// Country 변경 → cascading 초기화 (역방향 매칭 중에는 건너뜀)
watch(countryCode, (newC, oldC) => {
  if (newC === oldC || isReverseLookup.value) return;
  stateCode.value = '';
  cityCode.value = '';
  postcodeCode.value = '';
  foreignState.value = '';
  foreignCity.value = '';
  foreignPostcode.value = '';
  cityOptions.value = [];
  postcodeOptions.value = [];
});

// State → City
watch(stateCode, async (newState) => {
  if (isReverseLookup.value) return;
  cityCode.value = '';
  postcodeCode.value = '';
  cityOptions.value = [];
  postcodeOptions.value = [];
  if (!newState) return;
  loadingCities.value = true;
  try {
    const res: any = await entityMyApi.getMalaysiaCities(newState);
    const data = res?.data?.data || res?.data || [];
    cityOptions.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.warn('[StructuredAddressInput] load cities failed:', err);
  } finally {
    loadingCities.value = false;
  }
});

// City → Postcode
watch(cityCode, async (newCity) => {
  if (isReverseLookup.value) return;
  postcodeCode.value = '';
  postcodeOptions.value = [];
  if (!newCity) return;
  loadingPostcodes.value = true;
  try {
    const res: any = await entityMyApi.getMalaysiaPostcodes(newCity);
    const data = res?.data?.data || res?.data || [];
    postcodeOptions.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.warn('[StructuredAddressInput] load postcodes failed:', err);
  } finally {
    loadingPostcodes.value = false;
  }
});

// 모든 입력 변경 → compose 후 emit
watch(
  [line1, line2, line3, countryCode, stateCode, cityCode, postcodeCode, foreignState, foreignCity, foreignPostcode],
  () => {
    if (!editMode.value) return;
    const composed = composeAddress();
    if (composed !== props.modelValue) emit('update:modelValue', composed);
  },
);

/**
 * 합성된 주소 문자열을 best-effort 로 구조화 필드로 역파싱.
 *   포맷: "line1, line2, line3, postcode (or postcode city), city, state, country"
 */
function parseComposedAddress(text: string) {
  const out = { line1: '', line2: '', line3: '', country: '', state: '', city: '', postcode: '' };
  const parts = String(text || '')
    .split(',')
    .map((s) => s.trim().replace(/^[\s,.;]+|[\s,.;]+$/g, '').trim())
    .filter(Boolean);
  if (parts.length === 0) return out;
  if (parts.length === 1) { out.line1 = parts[0]; return out; }

  const remaining = [...parts];
  out.country = remaining.pop() || '';
  if (remaining.length > 0) out.state = remaining.pop() || '';

  if (remaining.length > 0) {
    const last = remaining[remaining.length - 1];
    const combined = last.match(/^(\d{4,6})\s+(.+)$/);
    if (combined) {
      out.postcode = combined[1];
      out.city = combined[2];
      remaining.pop();
    } else if (/^\d{4,6}$/.test(last)) {
      out.postcode = remaining.pop() || '';
      if (remaining.length > 0) out.city = remaining.pop() || '';
    } else {
      out.city = remaining.pop() || '';
      if (remaining.length > 0 && /^\d{4,6}$/.test(remaining[remaining.length - 1])) {
        out.postcode = remaining.pop() || '';
      }
    }
  }

  out.line1 = remaining[0] || '';
  out.line2 = remaining[1] || '';
  out.line3 = remaining.length > 3 ? remaining.slice(2).join(', ') : (remaining[2] || '');
  return out;
}

// Edit 진입 시 — 원본 값 백업 + 텍스트 파싱 + dropdown 매칭 (이름 → 코드 역추적).
async function enterEdit() {
  editMode.value = true;
  originalValue.value = props.modelValue || ''; // Cancel 복원용 스냅샷
  if (!props.modelValue) return;
  if (line1.value.trim() || line2.value.trim()) return; // 이미 사용자가 입력한 상태면 보존

  const parsed = parseComposedAddress(props.modelValue);
  line1.value = parsed.line1;
  line2.value = parsed.line2;
  line3.value = parsed.line3;

  // 역방향 매칭 전 구간 동안 cascading watcher 차단 — watcher 는 비동기이므로 마지막에 해제.
  isReverseLookup.value = true;
  try {
    // Country name → code 매칭
    if (parsed.country) {
      const target = parsed.country.toUpperCase().trim();
      const match = countryOptions.value.find((o) => o.CODE_NAME.toUpperCase().trim() === target);
      if (match) {
        countryCode.value = match.TOTAL_CODE;
        // watcher 가 비동기로 발화할 때 isReverseLookup=true 상태임을 보장하기 위해 nextTick 대기.
        await nextTick();
      }
    }

    // MY 인 경우 state/city/postcode 코드 매칭. 외국이면 free text 채움.
    if (countryCode.value === MALAYSIA_CODE) {
      if (parsed.state) {
        const targetState = parsed.state.toUpperCase().trim();
        const stMatch = stateOptions.value.find((o) => o.name.toUpperCase().trim() === targetState);
        if (stMatch) {
          stateCode.value = stMatch.code;
          // 즉시 cities 로드 (stateCode watcher 는 isReverseLookup 으로 차단되므로 직접 호출)
          try {
            const cres: any = await entityMyApi.getMalaysiaCities(stMatch.code);
            const cdata = cres?.data?.data || cres?.data || [];
            cityOptions.value = Array.isArray(cdata) ? cdata : [];
          } catch { /* ignore */ }
          // City match
          if (parsed.city) {
            const targetCity = parsed.city.toUpperCase().trim();
            const ctMatch = cityOptions.value.find((o) => o.name.toUpperCase().trim() === targetCity);
            if (ctMatch) {
              cityCode.value = ctMatch.code;
              // postcodes 로드
              try {
                const pres: any = await entityMyApi.getMalaysiaPostcodes(ctMatch.code);
                const pdata = pres?.data?.data || pres?.data || [];
                postcodeOptions.value = Array.isArray(pdata) ? pdata : [];
              } catch { /* ignore */ }
              // Postcode match
              if (parsed.postcode) {
                const pcMatch = postcodeOptions.value.find((o) => o.name === parsed.postcode || o.code === parsed.postcode);
                if (pcMatch) postcodeCode.value = pcMatch.code;
                else postcodeCode.value = parsed.postcode;
              }
            }
          }
        }
      }
    } else {
      foreignState.value = parsed.state;
      foreignCity.value = parsed.city;
      foreignPostcode.value = parsed.postcode;
    }
  } finally {
    isReverseLookup.value = false;
  }
}

/** Done — 현재 입력값(이미 v-model 에 합성됨) 유지하며 view 모드로 복귀. */
function confirmEdit() {
  editMode.value = false;
}
/** Cancel — Edit 진입 직전 값으로 되돌리고 구조화 필드도 초기화. */
function cancelEdit() {
  emit('update:modelValue', originalValue.value);
  editMode.value = false;
  // 구조화 필드 reset — 다음 Edit 진입 시 새로 파싱하도록.
  line1.value = ''; line2.value = ''; line3.value = '';
  countryCode.value = ''; stateCode.value = ''; cityCode.value = ''; postcodeCode.value = '';
  foreignState.value = ''; foreignCity.value = ''; foreignPostcode.value = '';
  cityOptions.value = []; postcodeOptions.value = [];
}

const localText = computed({
  get: () => props.modelValue,
  set: (v: string) => emit('update:modelValue', v),
});
</script>

<template>
  <div class="sai-wrap">
    <div class="sai-header">
      <span class="sai-label">{{ editMode && showLabel !== false ? label : '' }}</span>
      <div v-if="!editMode" class="sai-header-actions">
        <v-btn
          v-if="addresses && addresses.length > 0"
          variant="text"
          size="x-small"
          color="grey-darken-1"
          class="sai-history-btn"
          @click="emit('show-addresses')"
        >
          <v-icon size="14" start>mdi-history</v-icon>Address History
        </v-btn>
        <v-btn variant="text" size="x-small" color="primary" @click="enterEdit">
          <v-icon size="14" start>mdi-pencil</v-icon>Edit
        </v-btn>
      </div>
      <div v-else class="sai-header-actions">
        <v-tooltip location="top" :open-delay="150">
          <template #activator="{ props: tProps }">
            <v-btn v-bind="tProps" variant="text" size="x-small" color="grey-darken-1" icon @click="cancelEdit">
              <v-icon size="18">mdi-undo</v-icon>
            </v-btn>
          </template>
          <span>Cancel — revert to previous value</span>
        </v-tooltip>
        <v-btn variant="flat" size="x-small" color="primary" @click="confirmEdit">
          <v-icon size="14" start>mdi-check</v-icon>Done
        </v-btn>
      </div>
    </div>


    <!-- Default: textarea -->
    <div v-if="!editMode" class="sai-text-wrap">
      <v-textarea
        v-model="localText"
        :label="label"
        rows="2"
        auto-grow
        variant="outlined"
        density="compact"
        hide-details
        bg-color="white"
      >
        <template v-if="$slots['append-inner']" #append-inner>
          <slot name="append-inner" />
        </template>
      </v-textarea>
      <div
        v-if="draggingActive"
        class="sai-drop-overlay"
        @dragover.prevent
        @drop.prevent="(e) => {
          const json = e.dataTransfer?.getData('application/x-entity-field');
          let val = '';
          if (json) { try { val = JSON.parse(json)?.value || ''; } catch {} }
          if (!val) val = e.dataTransfer?.getData('text/plain') || '';
          if (val) emit('drop-fill', String(val));
        }"
      />
    </div>

    <!-- Structured form (cascading dropdowns) -->
    <div v-else class="sai-structured">
      <v-row dense>
        <v-col cols="12">
          <v-text-field v-model="line1" label="Address Line 1" variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12">
          <v-text-field v-model="line2" label="Address Line 2" variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12">
          <v-text-field v-model="line3" label="Address Line 3" variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="countryCode"
            :items="countryOptions"
            item-title="CODE_NAME"
            item-value="TOTAL_CODE"
            label="Country"
            placeholder="Search..."
            variant="outlined"
            density="compact"
            hide-details
            clearable
            auto-select-first
            :loading="loadingCountries"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-if="isMalaysiaSelected"
            v-model="stateCode"
            :items="stateOptions"
            item-title="name"
            item-value="code"
            label="State"
            placeholder="Search..."
            variant="outlined"
            density="compact"
            hide-details
            clearable
            auto-select-first
            :loading="loadingStates"
          />
          <v-text-field
            v-else
            v-model="foreignState"
            label="State"
            variant="outlined"
            density="compact"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-if="isMalaysiaSelected"
            v-model="cityCode"
            :items="cityOptions"
            item-title="name"
            item-value="code"
            label="City"
            placeholder="Search..."
            variant="outlined"
            density="compact"
            hide-details
            clearable
            auto-select-first
            :loading="loadingCities"
            :disabled="!stateCode"
          />
          <v-text-field
            v-else
            v-model="foreignCity"
            label="City"
            variant="outlined"
            density="compact"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-combobox
            v-if="isMalaysiaSelected"
            v-model="postcodeCode"
            :items="postcodeOptions"
            item-title="name"
            item-value="code"
            label="Postcode"
            placeholder="Type or select..."
            variant="outlined"
            density="compact"
            hide-details
            clearable
            auto-select-first
            :loading="loadingPostcodes"
            :return-object="false"
            :disabled="!cityCode"
          />
          <v-text-field
            v-else
            v-model="foreignPostcode"
            label="Postcode"
            variant="outlined"
            density="compact"
            hide-details
          />
        </v-col>
      </v-row>
      <div class="sai-preview">
        <span class="sai-preview-label">Preview:</span>
        <span class="sai-preview-text">{{ localText || '—' }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sai-wrap { margin-bottom: 4px; }
.sai-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 4px; padding: 0 2px;
}
.sai-label { font-size: 12px; color: #6B7280; font-weight: 500; }
.sai-header-actions { display: flex; align-items: center; gap: 4px; }
.sai-history-btn { opacity: 0.75; }
.sai-history-btn:hover { opacity: 1; }
.sai-text-wrap { position: relative; }
.sai-text-wrap :deep(.v-field) { background-color: #fff; }
.sai-text-wrap :deep(.v-field__overlay) { opacity: 0 !important; }
.sai-drop-overlay {
  position: absolute; inset: 0;
  border: 2px dashed #1B84FF; border-radius: 6px;
  background: rgba(27, 132, 255, 0.06);
  pointer-events: all; cursor: copy;
}
.sai-structured {
  border: 1px solid #D6E4FF; border-radius: 6px;
  padding: 10px 12px; background: #F8FAFE;
}
/* 구조화 모드 input 들을 흰 배경으로 — 부모 배경(#F8FAFE) 과 구분되어 입력 가능함이 분명히 보이도록. */
.sai-structured :deep(.v-field) {
  background-color: #fff;
  border-radius: 4px;
}
.sai-structured :deep(.v-field__overlay) {
  background-color: transparent;
}
.sai-preview {
  margin-top: 8px; padding: 6px 10px;
  background: #fff; border: 1px solid #E4E9EF; border-radius: 4px;
  font-size: 12px;
}
.sai-preview-label { color: #6B7280; font-weight: 600; margin-right: 6px; }
.sai-preview-text { color: #3c4650; word-break: break-all; }
</style>
